import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Slider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// Import your existing ColorWheel component
import ColorWheel from '../../components/ColorWheel';

const animationTypes = [
  'Fade',
  'Flash',
  'Erase',
  'Gradient',
  'Column Striped',
  'Row Striped',
];

const CustomAnimationDialog = ({ open, onClose, onSave, initialData }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState(animationTypes[0]);
  const [speed, setSpeed] = useState(5);
  const [colors, setColors] = useState(['rgb(255, 0, 0)']);
  const [activeColorIndex, setActiveColorIndex] = useState(0);

  // Reset form when dialog opens or initialData changes
  useEffect(() => {
    if (open) {
      setName(initialData?.name || '');
      setDescription(initialData?.description || '');
      setType(initialData?.type || animationTypes[0]);
      setSpeed(initialData?.speed || 5);
      setColors(initialData?.colors || ['rgb(255, 0, 0)']);
      setActiveColorIndex(0);
    }
  }, [initialData, open]);

  const updateColor = (newColor) => {
    const updated = [...colors];
    updated[activeColorIndex] = newColor;
    setColors(updated);
  };

  const addColor = () => {
    if (colors.length < 6) {
      setColors([...colors, 'rgb(255, 255, 255)']);
      setActiveColorIndex(colors.length);
    }
  };

  const removeColor = (index) => {
    if (colors.length > 1) {
      const updated = colors.filter((_, i) => i !== index);
      setColors(updated);
      setActiveColorIndex(Math.max(0, activeColorIndex - 1));
    }
  };

  const handleSave = () => {
    const customAnim = { name, description, type, speed, colors };
    if (onSave) onSave(customAnim);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {initialData ? 'Edit Custom Animation' : 'Create Custom Animation'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Animation Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Animation Type</InputLabel>
            <Select
              value={type}
              label="Animation Type"
              onChange={(e) => setType(e.target.value)}
            >
              {animationTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <Typography gutterBottom>Animation Speed: {speed}</Typography>
            <Slider
              value={speed}
              onChange={(e, v) => setSpeed(v)}
              valueLabelDisplay="auto"
              step={1}
              min={1}
              max={9}
            />
          </Box>

          {/* Color Selection */}
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Colors ({colors.length}/6)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {colors.map((c, idx) => (
              <Box
                key={idx}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border:
                    idx === activeColorIndex
                      ? '3px solid blue'
                      : '1px solid gray',
                  backgroundColor: c,
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onClick={() => setActiveColorIndex(idx)}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeColor(idx);
                  }}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    background: 'white',
                    boxShadow: 1,
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {colors.length < 6 && (
              <IconButton onClick={addColor}>
                <AddIcon />
              </IconButton>
            )}
          </Box>

          {/* Active color wheel */}
          {colors[activeColorIndex] && (
            <Box sx={{ flex: 1, maxWidth: '65%', mt: 2 }}>
              <ColorWheel
                color={colors[activeColorIndex]}
                onChange={updateColor}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomAnimationDialog;
